import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { UiButton } from "@domun/component-library";
import UiModal from "@dashboard/components/ui-modal/ui-modal";
import FieldAutocompleteAddress from "@dashboard/components/field-autocomplete-address";
import { PlatformCountry, ResponseMapType } from "@dashboard/common/types";
import { createGeocodedResult } from "@dashboard/utils/geocoder.utils";
import { DeliveryAddressMapModalWrapper } from "./delivery-address-map-modal.styled";

import { DeliveryMapConfirmResult } from "../../entities/delivery.entities";

type DeliveryAddressMapModalProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: (result: DeliveryMapConfirmResult) => void;
  initialAddress?: string;
  initialCity?: string;
  initialLat?: number;
  initialLng?: number;
  country?: PlatformCountry;
  originLat?: number;
  originLng?: number;
};

const DEFAULT_ZOOM = 16;
const hasValidCoords = (lat?: number, lng?: number) =>
  !!lat && !!lng && (lat !== 0 || lng !== 0);

const waitForGoogleMaps = (): Promise<typeof google.maps> =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      if (window.google?.maps) {
        resolve(window.google.maps);
        return;
      }
      attempts += 1;
      if (attempts > 40) {
        reject(new Error("Google Maps not loaded"));
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });

const forwardGeocode = (
  address: string
): Promise<google.maps.LatLngLiteral | null> =>
  new Promise((resolve) => {
    if (!window.google?.maps) {
      resolve(null);
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]?.geometry) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
        return;
      }
      resolve(null);
    });
  });

export const DeliveryAddressMapModal: FC<DeliveryAddressMapModalProps> = ({
  show,
  onClose,
  onConfirm,
  initialAddress = "",
  initialCity = "",
  initialLat,
  initialLng,
  country,
  originLat,
  originLng,
}) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const idleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUpdatingFromGeocodeRef = useRef(false);
  const initialCityRef = useRef(initialCity);
  initialCityRef.current = initialCity;

  const reverseGeocodeCenterRef = useRef<() => Promise<void>>(async () => {});
  reverseGeocodeCenterRef.current = async () => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    if (!center) return;

    const latLng = { lat: center.lat(), lng: center.lng() };
    isUpdatingFromGeocodeRef.current = true;
    try {
      const result = await createGeocodedResult(latLng);
      if (result?.coords) {
        setCoords(result.coords);
        if (result.fullAddress) {
          setResolvedAddress(result.fullAddress);
          setAddressInput(result.fullAddress);
        }
        if (result.locality) {
          setResolvedCity(result.locality);
        }
      }
    } finally {
      isUpdatingFromGeocodeRef.current = false;
    }
  };

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [addressInput, setAddressInput] = useState(initialAddress);
  const [resolvedAddress, setResolvedAddress] = useState(initialAddress);
  const [resolvedCity, setResolvedCity] = useState(initialCity);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    hasValidCoords(initialLat, initialLng)
      ? { lat: initialLat!, lng: initialLng! }
      : null
  );

  const panTo = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (!map) return;
    isUpdatingFromGeocodeRef.current = true;
    map.panTo({ lat, lng });
    setCoords({ lat, lng });
    setTimeout(() => {
      isUpdatingFromGeocodeRef.current = false;
    }, 300);
  }, []);

  const handleAutocompleteSelect = useCallback(
    (mapResult: ResponseMapType) => {
      const lat = mapResult.coords?.lat;
      const lng = mapResult.coords?.lng;
      if (!lat || !lng) return;

      setAddressInput(mapResult.fullAddress || "");
      setResolvedAddress(mapResult.fullAddress || "");
      if (mapResult.locality) {
        setResolvedCity(mapResult.locality);
      }
      panTo(lat, lng);
    },
    [panTo]
  );

  const handleAddressInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAddressInput(e.target.value);
  };

  const handleConfirm = () => {
    if (!coords?.lat || !coords?.lng) return;
    onConfirm({
      address: resolvedAddress || addressInput,
      city: resolvedCity || initialCityRef.current,
      lat: coords.lat,
      lng: coords.lng,
    });
  };

  useEffect(() => {
    if (!show) return;

    setAddressInput(initialAddress);
    setResolvedAddress(initialAddress);
    setResolvedCity(initialCity);
    setMapReady(false);
    setMapError(false);

    if (hasValidCoords(initialLat, initialLng)) {
      setCoords({ lat: initialLat!, lng: initialLng! });
    } else {
      setCoords(null);
    }
  }, [show, initialAddress, initialCity, initialLat, initialLng]);

  useEffect(() => {
    if (!show || !mapContainerRef.current) return;

    let cancelled = false;

    const initMap = async () => {
      try {
        const maps = await waitForGoogleMaps();
        if (cancelled || !mapContainerRef.current) return;

        let center: google.maps.LatLngLiteral | null = null;

        if (hasValidCoords(initialLat, initialLng)) {
          center = { lat: initialLat!, lng: initialLng! };
        } else if (initialCity && country?.name) {
          center = await forwardGeocode(`${initialCity}, ${country.name}`);
        } else if (hasValidCoords(originLat, originLng)) {
          center = { lat: originLat!, lng: originLng! };
        }

        if (!center) {
          center = { lat: 4.711, lng: -74.0721 };
        }

        const map = new maps.Map(mapContainerRef.current, {
          center,
          zoom: DEFAULT_ZOOM,
          gestureHandling: "greedy",
          scrollwheel: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        mapRef.current = map;
        setCoords(center);

        idleListenerRef.current = map.addListener("idle", () => {
          if (isUpdatingFromGeocodeRef.current) return;
          if (idleDebounceRef.current) {
            clearTimeout(idleDebounceRef.current);
          }
          idleDebounceRef.current = setTimeout(() => {
            reverseGeocodeCenterRef.current();
          }, 300);
        });

        if (!cancelled) {
          setMapReady(true);
        }
      } catch {
        if (!cancelled) {
          setMapError(true);
        }
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (idleDebounceRef.current) {
        clearTimeout(idleDebounceRef.current);
        idleDebounceRef.current = null;
      }
      idleListenerRef.current?.remove();
      idleListenerRef.current = null;
      mapRef.current = null;
      setMapReady(false);
    };
  }, [
    show,
    initialLat,
    initialLng,
    initialCity,
    country?.name,
    originLat,
    originLng,
  ]);

  return (
    <UiModal show={show} onClose={onClose} className="delivery-map-modal">
      <DeliveryAddressMapModalWrapper className="delivery-map-modal">
        <h3 className="map-modal-title">
          {t("delivery.create.location.mapTitle")}
        </h3>
        <p className="map-modal-hint">{t("delivery.create.location.mapHint")}</p>

        <div className="map-modal-address-field">
          <FieldAutocompleteAddress
            id="delivery-map-address"
            name="mapAddress"
            label={t("delivery.create.customerAddress")}
            labelOutside
            value={addressInput}
            country={country}
            isRequired
            autocomplete="off"
            placeholder={t(
              "profile.addressesSections.form.address.placeholder"
            )}
            maxLength={255}
            onChange={handleAutocompleteSelect}
            onChangeValue={(e: ChangeEvent<Element>) =>
              handleAddressInputChange(e as ChangeEvent<HTMLInputElement>)
            }
          />
        </div>

        <div className="map-container-wrapper">
          {mapError ? (
            <div className="map-loading-overlay">
              {t("delivery.create.validationError")}
            </div>
          ) : (
            <>
              <div ref={mapContainerRef} className="map-container" />
              {!mapReady && (
                <div className="map-loading-overlay">
                  {t("delivery.create.location.mapHint")}
                </div>
              )}
              <span className="map-center-pin" aria-hidden="true">
                📍
              </span>
            </>
          )}
        </div>

        {resolvedAddress && (
          <p className="resolved-address">{resolvedAddress}</p>
        )}

        <div className="map-modal-actions">
          <UiButton onClick={handleConfirm} disabled={!coords || mapError}>
            {t("delivery.create.location.mapConfirm")}
          </UiButton>
        </div>
      </DeliveryAddressMapModalWrapper>
    </UiModal>
  );
};
